import { type MouseEvent, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import type { KbArticle } from "@/lib/kb-articles";

const MOBILE_QUERY = "(max-width: 960px)";

// Subscribe the active-card view to `window.location.hash`. Using
// useSyncExternalStore keeps a single source of truth (the URL) and
// avoids the React-19-flagged `setState`-in-`useEffect` pattern that
// the previous initial-hash sync relied on. As a bonus, the active
// card now also reacts to back/forward navigation that changes the
// hash — the old version only read the hash once on mount.
function subscribeToHash(callback: () => void) {
  window.addEventListener("hashchange", callback);
  return () => window.removeEventListener("hashchange", callback);
}

function getHashSnapshot(): string {
  return window.location.hash.replace(/^#/, "");
}

function getServerHashSnapshot(): string {
  return "";
}

export function KbArticles({ articles }: { articles: KbArticle[] }) {
  const hash = useSyncExternalStore(
    subscribeToHash,
    getHashSnapshot,
    getServerHashSnapshot,
  );

  if (articles.length === 0) return null;

  const active = articles.find((a) => a.id === hash) ?? articles[0];

  function handleCardClick(e: MouseEvent<HTMLAnchorElement>, id: string) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
      return;
    if (window.matchMedia(MOBILE_QUERY).matches) return;
    e.preventDefault();
    history.replaceState(null, "", `#${id}`);
    // `replaceState` does not fire `hashchange`; dispatch it manually so
    // the useSyncExternalStore subscriber re-reads and the active card
    // updates. `Event("hashchange")` is portable across browsers — the
    // dedicated `HashChangeEvent` constructor isn't supported everywhere.
    window.dispatchEvent(new Event("hashchange"));
  }

  return (
    <div className="kb-grid">
      <div className="kb-list">
        {articles.map((a) => (
          <Link
            key={a.id}
            to={`/ajutor/${a.id}`}
            className={`kb-card${a.id === active.id ? " is-active" : ""}`}
            onClick={(e) => handleCardClick(e, a.id)}
          >
            <span className="kb-icon">
              <Icon name={a.icon} />
            </span>
            <span className="kb-text">
              <h3 className="kb-title">{a.title}</h3>
              <p className="kb-snippet">{a.snippet}</p>
            </span>
            <span className="kb-meta">{a.tag}</span>
          </Link>
        ))}
      </div>

      <article className="kb-detail">
        <div className="kb-detail-eyebrow">
          <Icon name="menu_book" />
          Articol din aplicație
        </div>
        <h3>{active.title}</h3>
        <div className="kb-intro">
          {active.intro.map((p, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
          ))}
        </div>
        {active.points.length > 0 && (
          <div className="kb-points">
            {active.points.map((p, i) => (
              <details
                key={`${active.id}-${i}`}
                className="kb-point"
                open={i === 0}
              >
                <summary>{p.q}</summary>
                {p.intro && <p dangerouslySetInnerHTML={{ __html: p.intro }} />}
                {p.bullets && p.bullets.length > 0 && (
                  <ul>
                    {p.bullets.map((b, j) => (
                      <li key={j}>
                        <span dangerouslySetInnerHTML={{ __html: b }} />
                      </li>
                    ))}
                  </ul>
                )}
              </details>
            ))}
          </div>
        )}
        <div className="kb-detail-foot">
          <span className="kb-tag">
            <Icon name="label" style={{ fontSize: 14 }} />
            {active.tag}
            {active.updated ? ` · Actualizat ${active.updated}` : ""}
          </span>
        </div>
      </article>
    </div>
  );
}
